import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Factory as FactoryIcon, AlertCircle, Printer, Save, Loader2, ChevronDown, Plus, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

// Import refactored components
import {
  UProfileVisual,
  PresetManagementModal,
  SpacerVisualization,
  DEFAULT_SPACER_STOCK,
  snapToValidSide,
  validateSetupDistance,
  getNextValidSide,
  snapToValidLip,
  validateLipGap,
  getNextValidLip,
  getNextValidThickness,
  calculateSpacers,
  calculateSheetWidth
} from './calculator';

const Calculator = ({ onLogout, editingProduct, onProductSaved }) => {
  // Form state
  const [selectedPreset, setSelectedPreset] = useState('');
  const [base, setBase] = useState('30');
  const [leftSide, setLeftSide] = useState('25');
  const [rightSide, setRightSide] = useState('25');
  const [thickness, setThickness] = useState('1.5');
  const [leftLip, setLeftLip] = useState('0');
  const [rightLip, setRightLip] = useState('0');
  
  // Refs to track latest values (for use in callbacks)
  const leftSideRef = useRef(leftSide);
  const rightSideRef = useRef(rightSide);
  const leftLipRef = useRef(leftLip);
  const rightLipRef = useRef(rightLip);
  const baseRef = useRef(base);
  
  // Keep refs in sync with state
  useEffect(() => { leftSideRef.current = leftSide; }, [leftSide]);
  useEffect(() => { rightSideRef.current = rightSide; }, [rightSide]);
  useEffect(() => { leftLipRef.current = leftLip; }, [leftLip]);
  useEffect(() => { rightLipRef.current = rightLip; }, [rightLip]);
  useEffect(() => { baseRef.current = base; }, [base]);
  
  // Calculation results
  const [result, setResult] = useState(null);
  const [spacers, setSpacers] = useState(null);
  const [errors, setErrors] = useState([]);
  
  // Presets state
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  
  // Preset management modal state
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({ label: '', base: '', leftSide: '', rightSide: '', leftLip: '', rightLip: '', thickness: '' });
  const [presetSaving, setPresetSaving] = useState(false);
  const [presetError, setPresetError] = useState('');
  
  // Spacer stock state
  const [spacerStock, setSpacerStock] = useState(DEFAULT_SPACER_STOCK);
  
  // Track if current values differ from selected preset (for Save button)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  // Snapshot of the originally-loaded preset values — used to detect unsaved
  // changes. State (not ref) so derived values can read it cleanly.
  const [originalPresetValues, setOriginalPresetValues] = useState(null);
  
  // Editable preset name
  const [presetName, setPresetName] = useState('');
  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);
  const [isNewPreset, setIsNewPreset] = useState(false);
  const [nameError, setNameError] = useState('');
  const nameInputRef = useRef(null);

  // Structured product identity (drives the auto-composed name).
  // We keep the legacy `presetName` state above as a computed mirror so the
  // existing "preset dropdown" UI still works during transition — but writes
  // always go through these structured fields.
  const [brand, setBrand] = useState('');
  const [parts, setParts] = useState([]);         // subset of ["L","Z","T"]
  const [partSize, setPartSize] = useState('');   // "G" | "N"
  const [variantNote, setVariantNote] = useState('');
  const [knownBrands, setKnownBrands] = useState([]);
  const [disp, setDisp] = useState(null);
  
  // Production state
  const [produceCount, setProduceCount] = useState('');
  // Optional backdated production date. Empty = use today (server defaults).
  // Lets the operator backfill historical production runs.
  const [produceDate, setProduceDate] = useState('');
  const [producing, setProducing] = useState(false);
  const [productionError, setProductionError] = useState('');
  const [productionSuccess, setProductionSuccess] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // ---------- Identity helpers ----------
  // Mirror of the backend's identity composer (routers/presets.py:_compute_identity).
  // Kept in sync intentionally — preview-only; server is the source of truth on write.
  const ALLOWED_PARTS = ['L', 'Z', 'T'];
  const normalizeParts = (arr) => ALLOWED_PARTS.filter(p => (arr || []).includes(p));
  const normalizeThickness = (t) => {
    if (t === null || t === undefined || t === '') return '';
    const f = parseFloat(String(t).replace(',', '.'));
    if (Number.isNaN(f)) return String(t);
    const s = f.toFixed(2).replace(/\.?0+$/, '');
    return s.includes('.') ? s : `${s}.0`;
  };
  const composeDisplayName = (b, ps, sz, th) => {
    const partsN = normalizeParts(ps);
    const partsStr = partsN.length ? partsN.join('/') : '?';
    const szN = (sz || '').toUpperCase();
    const thN = normalizeThickness(th);
    return [b, partsStr, szN, thN].filter(Boolean).join(' ').trim();
  };

  // Toggle a part membership (L / Z / T) — order is enforced on read.
  const togglePart = (p) => {
    setParts(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  // Start a new preset
  const startNewPresetMode = useCallback(() => {
    setSelectedPreset('');
    setPresetName('');
    setBrand('');
    setParts([]);
    setPartSize('');
    setVariantNote('');
    setIsNewPreset(true);
    setHasUnsavedChanges(true);
    setNameError('');
    setOriginalPresetValues(null);
    // Focus the brand input
    setTimeout(() => nameInputRef.current?.focus(), 100);
  }, []);

  // Handle editingProduct prop from Products page
  useEffect(() => {
    if (editingProduct) {
      // Load the product for editing
      setSelectedPreset(editingProduct.id);
      setPresetName(editingProduct.label);
      setBase(editingProduct.base.toString());
      setLeftSide(editingProduct.leftSide.toString());
      setRightSide(editingProduct.rightSide.toString());
      setLeftLip(editingProduct.leftLip.toString());
      setRightLip(editingProduct.rightLip.toString());
      setThickness(editingProduct.thickness.toString());
      setIsNewPreset(false);
      setOriginalPresetValues({
        label: editingProduct.label,
        base: editingProduct.base,
        leftSide: editingProduct.leftSide,
        rightSide: editingProduct.rightSide,
        leftLip: editingProduct.leftLip,
        rightLip: editingProduct.rightLip,
        thickness: editingProduct.thickness
      });
      setHasUnsavedChanges(false);
    } else if (editingProduct === null && onProductSaved) {
      // null means "new product" mode from Products page
      startNewPresetMode();
    }
  }, [editingProduct, onProductSaved, startNewPresetMode]);

  // Fetch presets on mount
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const response = await fetch(`${API_URL}/api/presets`);
        if (response.ok) {
          const data = await response.json();
          setPresets(data);
          // Auto-select first preset if available
          if (data.length > 0) {
            const firstPreset = data[0];
            setSelectedPreset(firstPreset.id);
            setPresetName(firstPreset.displayName || firstPreset.label || '');
            setBrand(firstPreset.brand || '');
            setParts(firstPreset.parts || []);
            setPartSize(firstPreset.partSize || '');
            setVariantNote(firstPreset.variantNote || '');
            setBase(firstPreset.base);
            setLeftSide(firstPreset.leftSide);
            setRightSide(firstPreset.rightSide);
            setLeftLip(firstPreset.leftLip);
            setRightLip(firstPreset.rightLip);
            setThickness(firstPreset.thickness);
            // Store original values for change detection
            setOriginalPresetValues({
              brand: firstPreset.brand || '',
              parts: firstPreset.parts || [],
              partSize: firstPreset.partSize || '',
              variantNote: firstPreset.variantNote || '',
              base: firstPreset.base,
              leftSide: firstPreset.leftSide,
              rightSide: firstPreset.rightSide,
              leftLip: firstPreset.leftLip,
              rightLip: firstPreset.rightLip,
              thickness: firstPreset.thickness
            });
          }
        }
      } catch (error) {
        console.error('Error fetching presets:', error);
      } finally {
        setPresetsLoading(false);
      }
    };

    fetchPresets();

    // Distinct brands for the combobox suggestions
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/presets/brands`);
        if (r.ok) setKnownBrands(await r.json());
      } catch (e) { console.warn('[Calculator] brand suggestions unavailable:', e); }
    })();

    // Variant-note visibility settings
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/settings/display`);
        if (r.ok) setDisp(await r.json());
      } catch (e) { /* defaults apply */ }
    })();
  }, [API_URL]);

  // Handle preset change
  const handlePresetChange = (presetId) => {
    setSelectedPreset(presetId);
    setIsNewPreset(false);
    setNameError('');
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      setPresetName(preset.displayName || preset.label || '');
      setBrand(preset.brand || '');
      setParts(preset.parts || []);
      setPartSize(preset.partSize || '');
      setVariantNote(preset.variantNote || '');
      setBase(preset.base);
      setLeftSide(preset.leftSide);
      setRightSide(preset.rightSide);
      setLeftLip(preset.leftLip);
      setRightLip(preset.rightLip);
      setThickness(preset.thickness);
      // Store original values to detect changes
      setOriginalPresetValues({
        brand: preset.brand || '',
        parts: preset.parts || [],
        partSize: preset.partSize || '',
        variantNote: preset.variantNote || '',
        base: preset.base,
        leftSide: preset.leftSide,
        rightSide: preset.rightSide,
        leftLip: preset.leftLip,
        rightLip: preset.rightLip,
        thickness: preset.thickness
      });
      setHasUnsavedChanges(false);
    }
    setPresetDropdownOpen(false);
  };
  
  // Check if values have changed from original preset.
  // useMemo (not useEffect) — derives a value, doesn't write state from a side effect.
  const computedHasUnsavedChanges = useMemo(() => {
    if (!selectedPreset || !originalPresetValues) return false;
    const orig = originalPresetValues;
    const sameParts = (orig.parts || []).join(',') === (parts || []).join(',');
    return (
      brand !== (orig.brand || '') ||
      !sameParts ||
      partSize !== (orig.partSize || '') ||
      variantNote !== (orig.variantNote || '') ||
      base !== orig.base ||
      leftSide !== orig.leftSide ||
      rightSide !== orig.rightSide ||
      leftLip !== orig.leftLip ||
      rightLip !== orig.rightLip ||
      thickness !== orig.thickness
    );
  }, [selectedPreset, originalPresetValues, brand, parts, partSize, variantNote, base, leftSide, rightSide, leftLip, rightLip, thickness]);
  useEffect(() => { setHasUnsavedChanges(computedHasUnsavedChanges); }, [computedHasUnsavedChanges]);
  
  // Save current values to selected preset (or create new)
  const saveCurrentToPreset = async () => {
    // Validate structured fields
    if (!brand.trim()) {
      setNameError('Brand is required');
      nameInputRef.current?.focus();
      return;
    }
    if (parts.length === 0) {
      setNameError('Pick at least one part (L / Z / T)');
      return;
    }
    if (!partSize) {
      setNameError('Pick a size (G = Wide / N = Tight)');
      return;
    }
    
    setNameError('');
    setSavingPreset(true);
    
    const payload = {
      brand: brand.trim(),
      parts,
      partSize,
      variantNote: variantNote.trim() || null,
      base, leftSide, rightSide, leftLip, rightLip, thickness,
    };
    try {
      if (isNewPreset) {
        const response = await fetch(`${API_URL}/api/presets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          const newPreset = await response.json();
          setPresets(prev => [...prev, newPreset]);
          setSelectedPreset(newPreset.id);
          setIsNewPreset(false);
          setOriginalPresetValues({
            brand: newPreset.brand, parts: newPreset.parts, partSize: newPreset.partSize,
            variantNote: newPreset.variantNote || '', base, leftSide, rightSide, leftLip, rightLip, thickness,
          });
          setHasUnsavedChanges(false);
          if (onProductSaved) onProductSaved();
        } else {
          const e = await response.json().catch(() => ({}));
          setNameError(e.detail || 'Save failed');
        }
      } else if (selectedPreset) {
        const response = await fetch(`${API_URL}/api/presets/${selectedPreset}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          const updated = await response.json();
          setPresets(prev => prev.map(p => p.id === selectedPreset ? updated : p));
          setOriginalPresetValues({
            brand: updated.brand, parts: updated.parts, partSize: updated.partSize,
            variantNote: updated.variantNote || '', base, leftSide, rightSide, leftLip, rightLip, thickness,
          });
          setHasUnsavedChanges(false);
          if (onProductSaved) onProductSaved();
        } else {
          const e = await response.json().catch(() => ({}));
          setNameError(e.detail || 'Save failed');
        }
      }
    } catch (error) {
      console.error('Error saving preset:', error);
    } finally {
      setSavingPreset(false);
    }
  };

  // ===== PRESET MANAGEMENT FUNCTIONS =====
  
  const openPresetModal = () => {
    setShowPresetModal(true);
    setEditingPreset(null);
    setPresetForm({ label: '', base: '', leftSide: '', rightSide: '', leftLip: '', rightLip: '', thickness: '' });
    setPresetError('');
  };

  const closePresetModal = () => {
    setShowPresetModal(false);
    setEditingPreset(null);
    setPresetForm({ label: '', base: '', leftSide: '', rightSide: '', leftLip: '', rightLip: '', thickness: '' });
    setPresetError('');
  };

  const startNewPreset = () => {
    setEditingPreset(null);
    setPresetForm({
      label: '',
      base: base,
      leftSide: leftSide,
      rightSide: rightSide,
      leftLip: leftLip,
      rightLip: rightLip,
      thickness: thickness
    });
    setPresetError('');
  };

  const startEditPreset = (preset) => {
    setEditingPreset(preset);
    setPresetForm({
      label: preset.label,
      base: preset.base,
      leftSide: preset.leftSide,
      rightSide: preset.rightSide,
      leftLip: preset.leftLip,
      rightLip: preset.rightLip,
      thickness: preset.thickness
    });
    setPresetError('');
  };

  const cancelEdit = () => {
    setEditingPreset(null);
    setPresetForm({ label: '', base: '', leftSide: '', rightSide: '', leftLip: '', rightLip: '', thickness: '' });
    setPresetError('');
  };

  const savePreset = async () => {
    if (!presetForm.label.trim()) {
      setPresetError('Please enter a preset name');
      return;
    }

    setPresetSaving(true);
    setPresetError('');

    try {
      const presetData = {
        label: presetForm.label.trim(),
        base: presetForm.base,
        leftSide: presetForm.leftSide,
        rightSide: presetForm.rightSide,
        leftLip: presetForm.leftLip,
        rightLip: presetForm.rightLip,
        thickness: presetForm.thickness
      };

      let response;
      if (editingPreset) {
        response = await fetch(`${API_URL}/api/presets/${editingPreset.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(presetData)
        });
      } else {
        response = await fetch(`${API_URL}/api/presets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(presetData)
        });
      }

      if (response.ok) {
        const presetsResponse = await fetch(`${API_URL}/api/presets`);
        if (presetsResponse.ok) {
          const data = await presetsResponse.json();
          setPresets(data);
        }
        cancelEdit();
      } else {
        const errorData = await response.json();
        setPresetError(errorData.detail || 'Failed to save preset');
      }
    } catch (error) {
      setPresetError('Error saving preset: ' + error.message);
    } finally {
      setPresetSaving(false);
    }
  };

  const deletePreset = async (presetId) => {
    if (!window.confirm('Are you sure you want to delete this preset?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/presets/${presetId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const presetsResponse = await fetch(`${API_URL}/api/presets`);
        if (presetsResponse.ok) {
          const data = await presetsResponse.json();
          setPresets(data);
        }
        if (selectedPreset === presetId) {
          setSelectedPreset('');
        }
      } else {
        alert('Failed to delete preset');
      }
    } catch (error) {
      alert('Error deleting preset: ' + error.message);
    }
  };

  // ===== INPUT HANDLERS WITH VALIDATION =====

  const handleSideBlur = (value, setter) => {
    const snapped = snapToValidSide(value, thickness);
    setter(snapped);
  };

  const handleLipBlur = (value, setter) => {
    const snapped = snapToValidLip(value, thickness, base);
    setter(snapped);
  };

  const handleThicknessChange = (newThickness) => {
    setThickness(newThickness);
    
    // Use refs to get latest values (avoids stale closure issue)
    const currentLeftSide = leftSideRef.current;
    const currentRightSide = rightSideRef.current;
    const currentLeftLip = leftLipRef.current;
    const currentRightLip = rightLipRef.current;
    const currentBase = baseRef.current;
    
    // Auto-adjust sides and lips to valid values for new thickness
    const newLeftSide = snapToValidSide(currentLeftSide, newThickness);
    const newRightSide = snapToValidSide(currentRightSide, newThickness);
    const newLeftLip = snapToValidLip(currentLeftLip, newThickness, currentBase);
    const newRightLip = snapToValidLip(currentRightLip, newThickness, currentBase);
    
    if (newLeftSide !== currentLeftSide) setLeftSide(newLeftSide);
    if (newRightSide !== currentRightSide) setRightSide(newRightSide);
    if (newLeftLip !== currentLeftLip) setLeftLip(newLeftLip);
    if (newRightLip !== currentRightLip) setRightLip(newRightLip);
  };

  const handleBaseChange = (value) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      const rounded = Math.round(num * 2) / 2;
      setBase(rounded.toFixed(1));
    } else {
      setBase(value);
    }
  };

  // ===== CALCULATION EFFECT =====

  useEffect(() => {
    const baseNum = parseFloat(base);
    const leftNum = parseFloat(leftSide);
    const rightNum = parseFloat(rightSide);
    const thicknessNum = parseFloat(thickness);

    // Validate inputs
    const newErrors = [];
    
    if (isNaN(baseNum) || baseNum <= 0) newErrors.push('Invalid base value');
    if (isNaN(leftNum) || leftNum <= 0) newErrors.push('Invalid left side value');
    if (isNaN(rightNum) || rightNum <= 0) newErrors.push('Invalid right side value');
    if (isNaN(thicknessNum) || thicknessNum <= 0) newErrors.push('Invalid thickness value');

    // Validate setup distances
    const leftError = validateSetupDistance(leftSide, thickness, 'Left Side');
    const rightError = validateSetupDistance(rightSide, thickness, 'Right Side');
    if (leftError) newErrors.push(leftError);
    if (rightError) newErrors.push(rightError);
    
    // Validate lip gaps
    const leftLipError = validateLipGap(leftLip, thickness, 'Left Lip', base);
    const rightLipError = validateLipGap(rightLip, thickness, 'Right Lip', base);
    if (leftLipError) newErrors.push(leftLipError);
    if (rightLipError) newErrors.push(rightLipError);

    setErrors(newErrors);

    if (newErrors.length === 0 && !isNaN(baseNum) && !isNaN(leftNum) && !isNaN(rightNum) && !isNaN(thicknessNum)) {
      const calcResult = calculateSheetWidth(base, leftSide, rightSide, leftLip, rightLip, thickness);
      setResult(calcResult);
      
      const spacerResult = calculateSpacers(base, leftSide, rightSide, leftLip, rightLip, thickness, parseFloat(calcResult.requiredSheet));
      setSpacers(spacerResult);
    } else {
      setResult(null);
      setSpacers(null);
    }
  }, [base, leftSide, rightSide, leftLip, rightLip, thickness]);

  // Handle production
  const handleProduce = async () => {
    if (!result || !selectedPreset || !produceCount) {
      setProductionError('Select a product and enter count');
      return;
    }
    
    setProducing(true);
    setProductionError('');
    setProductionSuccess('');
    
    try {
      const token = localStorage.getItem('auth_token');
      const { status, body } = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/product-stock/produce`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
        xhr.onerror = () => reject(new Error('Network error'));
        // Send the CURRENT dims (what's on screen) so production matches
        // exactly what the user sees, even if the preset wasn't re-saved.
        xhr.send(JSON.stringify({
          productId: selectedPreset,
          quantity: parseInt(produceCount),
          base: parseFloat(base) || undefined,
          leftSide: parseFloat(leftSide) || undefined,
          rightSide: parseFloat(rightSide) || undefined,
          leftLip: parseFloat(leftLip) || 0,
          rightLip: parseFloat(rightLip) || 0,
          thickness: parseFloat(thickness) || undefined,
          effectiveAt: produceDate || undefined,
        }));
      });
      
      let data = null;
      try { data = body ? JSON.parse(body) : null; } catch (e) { console.debug('[produce] non-JSON body:', e?.message); }
      
      if (status >= 200 && status < 300) {
        const coilNote = (data?.portions?.length ?? 0) > 1 ? ` across ${data.portions.length} coils` : '';
        setProductionSuccess(`+${produceCount} produced (${(data?.weightUsed ?? 0).toFixed(1)}kg used${coilNote})${produceDate ? ` · ${produceDate}` : ''}`);
        setProduceCount('');
        setTimeout(() => setProductionSuccess(''), 3500);
      } else {
        // Structured 409 from the produced_before_coil_arrival check — friendly message
        const detail = data?.detail;
        if (detail && typeof detail === 'object' && detail.error === 'produced_before_coil_arrival') {
          setProductionError(`Coil arrived ${detail.coilArrivalDate} — can't produce earlier than that.`);
        } else {
          setProductionError(detail?.message || (typeof detail === 'string' ? detail : null) || body || `Production failed (HTTP ${status})`);
        }
      }
    } catch (err) {
      console.error('Production error:', err);
      setProductionError(err.message || 'Network error');
    } finally {
      setProducing(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 py-4 sm:py-8 px-2 sm:px-4 print:bg-white print:py-2">
      <div className="max-w-4xl mx-auto">
        {/* Single Card with Profile Config and Results */}
        <div className="space-y-4">
          <Card className="shadow-lg bg-zinc-900 border-zinc-800 print:bg-white print:border-slate-200">
            <CardContent className="p-2 sm:p-4 pt-2 sm:pt-4">
              {/* Results Display - AT THE TOP */}
              {result && (
                <div className="bg-zinc-800 text-white rounded-lg p-3 sm:p-4 mb-4 border border-zinc-700 print:bg-white print:text-zinc-900 print:border-slate-300">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-xs sm:text-sm text-zinc-400 print:text-slate-600">Required Sheet Width</div>
                      <div className="text-2xl sm:text-3xl font-bold">{result.requiredSheet} mm</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs sm:text-sm text-zinc-400 print:text-slate-600">Weight (6m profile)</div>
                      <div className="text-xl sm:text-2xl font-bold">{result.weightFor6m} kg</div>
                    </div>
                    
                    {/* Production Controls - hidden in print */}
                    {selectedPreset && (
                      <div className="flex flex-wrap items-center gap-2 ml-4 no-print">
                        <Input
                          type="number"
                          min="1"
                          value={produceCount}
                          onChange={(e) => {
                            setProduceCount(e.target.value);
                            setProductionError('');
                          }}
                          placeholder="Count"
                          className="w-20 h-9 bg-zinc-700 border-zinc-600 text-white text-center"
                          data-testid="produce-count-input"
                        />
                        {/* Optional production date — leave blank for today.
                            Used to backfill historical production. */}
                        <Input
                          type="date"
                          value={produceDate}
                          onChange={(e) => { setProduceDate(e.target.value); setProductionError(''); }}
                          max={new Date().toISOString().slice(0, 10)}
                          title="Production date — leave blank for today (used for backfilling history)"
                          className="h-9 bg-zinc-700 border-zinc-600 text-zinc-200 text-xs w-36"
                          data-testid="produce-date-input"
                        />
                        <Button
                          onClick={handleProduce}
                          disabled={!produceCount || producing}
                          className="h-9 bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                          data-testid="produce-btn"
                        >
                          {producing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <ArrowRight className="w-4 h-4 mr-1" />
                              Produce
                            </>
                          )}
                        </Button>
                        {productionSuccess && (
                          <span className="text-green-400 text-sm">{productionSuccess}</span>
                        )}
                        {productionError && (
                          <span className="text-red-400 text-sm">{productionError}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Preset Selector */}
              <div className="mb-4 pb-4 border-b border-zinc-700 no-print">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-zinc-300">
                    Product Configuration
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.print()}
                      className="text-xs gap-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                      data-testid="print-plan-btn"
                    >
                      <Printer className="w-3 h-3" />
                      Print Plan
                    </Button>
                  </div>
                </div>

                {/* Structured identity fields — brand / parts / size / variant note.
                    The display name is auto-composed below as read-only preview. */}
                <div className="bg-zinc-800/40 rounded-md border border-zinc-700 p-3 mb-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      ref={nameInputRef}
                      type="text"
                      value={brand}
                      onChange={(e) => { setBrand(e.target.value); if (nameError) setNameError(''); }}
                      placeholder="Brand (e.g. Salamander, Komm/Troc)"
                      list="known-brands-list"
                      className="flex-1 min-w-[180px] h-9 bg-zinc-800 border-zinc-700 text-zinc-200"
                      data-testid="preset-brand-input"
                    />
                    <datalist id="known-brands-list">
                      {knownBrands.map(b => <option key={b} value={b} />)}
                    </datalist>
                    {/* Parts toggles */}
                    <div className="flex gap-1" data-testid="preset-parts-group">
                      {['L', 'Z', 'T'].map(p => (
                        <Button
                          key={p}
                          type="button"
                          size="sm"
                          variant={parts.includes(p) ? 'default' : 'outline'}
                          onClick={() => togglePart(p)}
                          className={`h-9 w-9 px-0 ${parts.includes(p) ? 'bg-amber-600 hover:bg-amber-700 text-white border-amber-600' : 'border-zinc-700 text-zinc-300 hover:bg-zinc-700 bg-zinc-800'}`}
                          data-testid={`preset-part-${p}`}
                        >
                          {p}
                        </Button>
                      ))}
                    </div>
                    {/* Size toggle: G = Wide, N = Tight */}
                    <div className="flex gap-1" data-testid="preset-size-group">
                      {[
                        { key: 'G', label: 'G', title: 'Gjerë / Wide / Big' },
                        { key: 'N', label: 'N', title: 'Ngushtë / Tight / Small' },
                      ].map(s => (
                        <Button
                          key={s.key}
                          type="button"
                          size="sm"
                          title={s.title}
                          variant={partSize === s.key ? 'default' : 'outline'}
                          onClick={() => setPartSize(s.key)}
                          className={`h-9 px-3 ${partSize === s.key ? 'bg-sky-600 hover:bg-sky-700 text-white border-sky-600' : 'border-zinc-700 text-zinc-300 hover:bg-zinc-700 bg-zinc-800'}`}
                          data-testid={`preset-size-${s.key}`}
                        >
                          {s.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={variantNote}
                      onChange={(e) => setVariantNote(e.target.value)}
                      placeholder="Variant note (optional, e.g. 'No Lip')"
                      className="flex-1 h-8 bg-zinc-800 border-zinc-700 text-zinc-300 text-sm"
                      data-testid="preset-variant-note"
                    />
                    {/* Live name preview */}
                    <div className="text-xs text-zinc-400 px-2" data-testid="preset-name-preview">
                      <span className="text-zinc-500">Name: </span>
                      <span className="font-medium text-amber-400">
                        {composeDisplayName(brand, parts, partSize, thickness) || '—'}
                        {(disp?.variantNoteFactory ?? true) && variantNote ? <span className="text-zinc-400 ml-1">({variantNote})</span> : null}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Popover open={presetDropdownOpen} onOpenChange={setPresetDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 flex-1 justify-between border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-3"
                        disabled={presetsLoading}
                        data-testid="preset-dropdown-btn"
                      >
                        <span className="truncate">
                          {(() => {
                            if (!selectedPreset) return isNewPreset ? 'New product' : 'Pick a product…';
                            const p = presets.find(x => x.id === selectedPreset);
                            if (!p) return 'Pick a product…';
                            const name = p.displayName || p.label || 'Pick a product…';
                            return (disp?.variantNoteFactory ?? true) && p.variantNote ? `${name} (${p.variantNote})` : name;
                          })()}
                        </span>
                        <ChevronDown className="w-4 h-4 text-zinc-400 ml-2 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-1 bg-zinc-800 border-zinc-700" align="start">
                      <div className="max-h-72 overflow-y-auto">
                        {(() => {
                          // Group by structured brand (with legacy fallback)
                          const grouped = presets.reduce((acc, preset) => {
                            const b = preset.brand || (preset.label || '').split(' ')[0] || 'Other';
                            if (!acc[b]) acc[b] = [];
                            acc[b].push(preset);
                            return acc;
                          }, {});
                          return Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([b, items]) => (
                            <div key={b}>
                              <div className="px-3 py-1.5 text-xs text-zinc-500 font-medium">{b}</div>
                              {items.map((preset) => (
                                <button
                                  key={preset.id}
                                  onClick={() => handlePresetChange(preset.id)}
                                  className={`w-full text-left px-3 py-1.5 text-sm rounded hover:bg-zinc-700 ${selectedPreset === preset.id ? 'bg-zinc-700 text-white' : 'text-zinc-300'}`}
                                  data-testid={`preset-option-${preset.id}`}
                                >
                                  {preset.displayName
                                    ? preset.displayName.replace(preset.brand ? `${preset.brand} ` : '', '')
                                    : (preset.label || '').split(' ').slice(1).join(' ')}
                                  {(disp?.variantNoteFactory ?? true) && preset.variantNote ? <span className="ml-1 text-xs text-zinc-500">({preset.variantNote})</span> : null}
                                </button>
                              ))}
                            </div>
                          ));
                        })()}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={startNewPresetMode}
                    className="h-9 px-3 border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                    data-testid="new-preset-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveCurrentToPreset}
                    disabled={(!hasUnsavedChanges && !isNewPreset) || savingPreset}
                    className={`h-9 px-3 ${(hasUnsavedChanges || isNewPreset) ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'}`}
                    data-testid="save-preset-changes-btn"
                  >
                    {savingPreset ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {nameError && (
                  <p className="text-xs text-red-400 mt-1" data-testid="preset-name-error">{nameError}</p>
                )}
              </div>

              {/* Interactive Profile Visual */}
              <UProfileVisual
                base={base}
                leftSide={leftSide}
                rightSide={rightSide}
                thickness={thickness}
                leftLip={leftLip}
                rightLip={rightLip}
                onBaseChange={handleBaseChange}
                onLeftChange={setLeftSide}
                onRightChange={setRightSide}
                onLeftBlur={(val) => handleSideBlur(val, setLeftSide)}
                onRightBlur={(val) => handleSideBlur(val, setRightSide)}
                onThicknessChange={handleThicknessChange}
                onLeftLipChange={setLeftLip}
                onRightLipChange={setRightLip}
                onLeftLipBlur={(val) => handleLipBlur(val, setLeftLip)}
                onRightLipBlur={(val) => handleLipBlur(val, setRightLip)}
                snapToValidSide={snapToValidSide}
                getNextValidSide={getNextValidSide}
                getNextValidLip={getNextValidLip}
                getNextValidThickness={getNextValidThickness}
                currentThickness={thickness}
                currentBase={base}
              />

              {/* Errors */}
              {errors.length > 0 && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc pl-4 space-y-1">
                      {errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Spacer Visualizations */}
              {result && spacers && (
                <div className="mt-4">
                  <SpacerVisualization 
                    spacers={spacers} 
                    result={result} 
                    spacerStock={spacerStock}
                    onStockChange={setSpacerStock}
                  />
                </div>
              )}

              {/* Empty State */}
              {result === null && errors.length === 0 && (
                <div className="text-center py-8 text-zinc-500">
                  <FactoryIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Enter all dimensions to calculate required sheet width</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preset Management Modal */}
      <PresetManagementModal
        showModal={showPresetModal}
        onClose={closePresetModal}
        presets={presets}
        editingPreset={editingPreset}
        presetForm={presetForm}
        setPresetForm={setPresetForm}
        presetSaving={presetSaving}
        presetError={presetError}
        onStartNewPreset={startNewPreset}
        onStartEditPreset={startEditPreset}
        onCancelEdit={cancelEdit}
        onSavePreset={savePreset}
        onDeletePreset={deletePreset}
      />
    </div>
  );
};

export default Calculator;
